import classnames from 'classnames';
import * as R from 'ramda';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useBanner } from '@xingternal/banner';
import { trackEvent } from '@xingternal/hops-tracking';
import { IconButterflyMono } from '@xingternal/icons';
import { XingFrameContext } from '@xingternal/xing-frame-provider';

import { JobDetailContext } from '@/contexts';
import {
  EmailApplication,
  JobApplication,
  MessageApplication,
  UrlApplication,
  VisibleJob,
  XingId,
  useJobActionsLazyQuery,
  useJobActionsQuery,
  useJobCreateApplyIntentionMutation,
} from '@/graphql/generated';
import { trackNwtEvent } from '@/helpers/nwt-tracking';
import { appendQueryParams } from '@/helpers/query-string';
import { hasTrufflsOrigin, trufflsOriginQueryParam } from '@/helpers/truffls-origin';
import truncate from '@/helpers/truncate';
import { useBreakpoints, useCrateFormExperiment, useLoginStates } from '@/hooks';

import addTrackingParams from '../../addTrackingParams';

import styles from './apply-action.css';
import * as S from './apply-action.styles';

type ApplicationButtonProps = {
  href?: string;
  onClick: () => void;
  target?: string;
};

type XingApplyButtonProps = {
  type: string;
  icon?: React.FC;
  loading: boolean;
  className: string;
  onClick: () => void;
  'data-testid'?: string;
};

type Props = {
  // isPaidJob is needed for ABACUS-420
  isPaidJob: boolean;
  isSticky?: boolean;
  className?: string;
};

const applicationTypeNwtContext = {
  EasyApplication: 'easy_apply', // this is already deprecated but we keep it around for backward compatibility
  EmailApplication: 'email_apply',
  JobXingApplication: 'instant_apply',
  MessageApplication: 'private_message_apply',
  UrlApplication: 'url_apply',
};

const ApplyAction: React.FC<Props & WrappedComponentProps> = ({ className, intl, isPaidJob, isSticky }) => {
  const history = useHistory();
  const { id, slug } = useContext(JobDetailContext);
  const user = useContext(XingFrameContext).user as unknown as XingId;
  const { showBanner } = useBanner();
  const { isLoggedIn } = useLoginStates();
  const { search: locationSearch } = useLocation();
  const { isMobile } = useBreakpoints();
  // ABACUS-420 Crate step by step form redirection
  const { isCrateFormRedirection } = useCrateFormExperiment(isPaidJob);
  const [isRedirectingToCrate, setIsRedirectingToCrate] = useState(false);

  const isWideButton = !isMobile && !isSticky;

  const {
    loading: jobActionsLoading,
    error: jobActionsError,
    data: jobActionsData,
  } = useJobActionsQuery({
    variables: { slug },
  });
  const [
    getJobActions,
    {
      loading: lazyJobActionsLoading,
      called: lazyJobActionsCalled,
      data: lazyJobActionsData,
      error: lazyJobActionsError,
    },
  ] = useJobActionsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [jobCreateApplyIntentionMutation] = useJobCreateApplyIntentionMutation({
    variables: {
      id,
    },
  });
  const { jobCode, title, application } =
    jobActionsData?.job?.__typename === 'VisibleJob' ? jobActionsData.job : ({} as VisibleJob);
  const isXINGApplication = application?.__typename === 'JobXingApplication';
  const isExternalURLApplication = application?.__typename === 'UrlApplication';

  const trackApplyIntention = (): void => {
    jobCreateApplyIntentionMutation();

    if (!application?.__typename) return;

    const context = applicationTypeNwtContext[application.__typename];

    trackNwtEvent({
      channel: isMobile ? 'webm' : 'web',
      user_id: user.id,
      event: 'intended',
      intention: 'apply',
      page: 'jobs/detail',
      item_urn: `surn:x-xing:jobs:posting:${id}`,
      element: 'apply_button',
      context,
    });
  };

  const withApplyIntentionTracking: (fn: () => void) => () => void =
    (fn) =>
    (...args): void => {
      fn(...args);
      trackApplyIntention();
    };

  useEffect(() => {
    if (lazyJobActionsCalled && !lazyJobActionsLoading) {
      const showErrorBanner = (messageId: string): void => {
        showBanner({
          children: <FormattedMessage id={messageId} />,
          variant: 'error',
        });
      };

      if (lazyJobActionsError) {
        showErrorBanner('ERROR_SOMETHING_WENT_WRONG');

        return;
      }

      const isVisibleJob = lazyJobActionsData?.job?.__typename === 'VisibleJob';
      if (!isVisibleJob) {
        showErrorBanner('JOBS_POSTING_XING_APPLY_DEACTIVATED_ERROR');

        return;
      }

      const isXingApplication =
        (lazyJobActionsData?.job as VisibleJob)?.application?.__typename === 'JobXingApplication';

      if (!isXingApplication) {
        showErrorBanner('JOBS_POSTING_XING_APPLY_TYPE_ERROR');

        return;
      }

      trackApplyIntention();

      if (isLoggedIn || !isCrateFormRedirection) {
        history.push(
          appendQueryParams(`/jobs/${slug}/application`, {
            sc_o: 'jobs_apply_start',
            ...(hasTrufflsOrigin(locationSearch) ? trufflsOriginQueryParam : {}),
          })
        );
      } else {
        setIsRedirectingToCrate(true);
        window.location.assign(`/jobs/apply/${slug}?sc_o=jobs_apply_start`);
      }
    }
    // We only perform the effect when useJobActionsLazyQuery gets updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyJobActionsCalled, lazyJobActionsLoading, lazyJobActionsData, lazyJobActionsError]);

  if (jobActionsLoading || jobActionsError || jobActionsData?.job?.__typename !== 'VisibleJob') return null;

  const handleLinkApply = withApplyIntentionTracking((): void => {
    const { applyUrl } = application as UrlApplication;
    trackEvent('PropTrackAction', {
      PropTrackAction: `jobs_click_application_form_${applyUrl ? 'url' : 'email'}`,
      PropJobsApplyId: uuidv4(),
    });
  });

  const handleMessengerApply = withApplyIntentionTracking((): void => {
    const { contactUserId } = application as MessageApplication;
    window.location.assign(`/chats/new/${contactUserId}?contextId=jobs_apply&entryPoint=jobs_job_apply_message`);
    trackEvent('PropTrackAction', {
      PropTrackAction: 'jobs_click_application_form_xing_message',
      PropJobsApplyId: uuidv4(),
    });
  });

  const handleXingApply = (): void => {
    // track apply button click event for LO xing apply:
    !isLoggedIn &&
      trackEvent('PropTrackAction', {
        PropTrackAction: 'jobs_apply_start',
      });

    getJobActions({ variables: { slug } });
  };

  const buildMailtoURI = (applyEmail: string): string => {
    const subject = encodeURIComponent(
      intl.formatMessage(
        { id: `JOB_POSTING_APPLY_MAIL_${jobCode ? 'SUBJECT_WITH_CODE' : 'SUBJECT'}` },
        { posting_function: truncate(50)(title), posting_job_code: jobCode }
      )
    );
    const body = encodeURIComponent(
      intl.formatMessage(
        { id: 'JOB_POSTING_APPLY_MAIL_BODY' },
        { posting_function: title, name: R.prop('displayName', user) }
      )
    );

    return `mailto:${applyEmail}?subject=${subject}&body=${body}`;
  };

  const getApplicationButtonProps = (application: JobApplication): ApplicationButtonProps => {
    const { applyUrl } = application as UrlApplication;
    const { applyEmail } = application as EmailApplication;
    const hasApplyLink = applyUrl || applyEmail;

    const applyLinkProps = {
      href: applyEmail ? buildMailtoURI(applyEmail) : applyUrl && addTrackingParams(applyUrl),
      onClick: handleLinkApply,
      ...(applyUrl && { target: '_blank' }),
    };

    const messengerApplyProps = {
      onClick: handleMessengerApply,
    };

    return hasApplyLink ? applyLinkProps : messengerApplyProps;
  };

  const getXingApplicationButtonProps = (): XingApplyButtonProps => {
    import(/* webpackPrefetch: true */ '@/pages/job-application-page');

    return {
      type: 'button',
      icon: IconButterflyMono,
      loading: lazyJobActionsLoading || isRedirectingToCrate,
      onClick: handleXingApply,
      'data-testid': 'xing-application-action',
      className: classnames(className, { [styles['loading-icon-button']]: lazyJobActionsLoading }),
    };
  };

  const applyButtonProps = isXINGApplication
    ? getXingApplicationButtonProps()
    : application
    ? getApplicationButtonProps(application)
    : null;

  const getApplyButtonCopy = (): string => {
    if (isXINGApplication) return 'JOB_POSTING_XING_APPLY_BUTTON';
    if (isExternalURLApplication) return 'JOB_POSTING_APPLY_EXTERNAL_URL';

    return 'JOB_POSTING_APPLY_CRAWLED_BUTTON_FORM_ACTION_LIST';
  };

  return (
    <S.StyledApplyAction
      $isWideButton={isWideButton}
      variant="primary"
      size="medium"
      className={className}
      data-testid="applyAction"
      data-cy="apply-action"
      {...applyButtonProps}
      data-soft-login-fenced
    >
      <FormattedMessage id={getApplyButtonCopy()} />
    </S.StyledApplyAction>
  );
};

export default injectIntl(ApplyAction);
